import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Save, Loader2, Settings as SettingsIcon, Sparkles, Globe, Plus, X, GripVertical,
  Package, Layers, Users, Check, ShieldCheck,
} from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Toggle } from '@/components/ui/Toggle'
import { Textarea } from '@/components/ui/Textarea'
import { NumberInput } from '@/components/ui/NumberInput'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { FileUpload } from '@/components/ui/FileUpload'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { useZodForm } from '@/hooks/useZodForm'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { PlansService, type Plan } from '@/services/modules/PlansService'
import { ModalitiesService, type Modality } from '@/services/modules/ModalitiesService'
import { ProductsService, type Product } from '@/services/modules/ProductsService'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { useAuthority } from '@/hooks/useAuthority'

/**
 * Form de cadastro/edição de Plano em PÁGINA DEDICADA (regra global do Comercial).
 *
 * Seções (FormSidemenu):
 *   1) Dados            (sempre)
 *   2) Página de vendas (CONDICIONAL — só aparece com displayWebsite=true)
 *   3) Workflow         (switches)
 *
 * Uploads (imagem e PDF) só ficam disponíveis em edição (precisam de plan.id).
 * No cadastro novo, o usuário primeiro salva os dados básicos; depois entra
 * em edição para enviar arquivos.
 */
const schema = z.object({
  name:                z.string().trim().min(1, 'Informe o nome do plano.').max(150, 'O nome não pode passar de 150 caracteres.'),
  productId:           z.string().min(1, 'Selecione o produto.'),
  price:               z.number({ message: 'Informe o valor do plano.' }).min(0, 'O valor não pode ser negativo.'),
  planType:            z.enum(['PESSOA FÍSICA', 'PESSOA JURÍDICA']),
  modalityId:          z.string().min(1, 'Selecione a modalidade.'),
  status:              z.enum(['ATIVO', 'INATIVO']),

  // Workflow
  displayWebsite:                     z.boolean(),

  // Página de vendas — todos opcionais no schema; superRefine torna obrigatórios
  // quando displayWebsite=true (incluindo presença de Imagem e Contrato).
  lpName:     z.string().max(150, 'Máximo 150 caracteres.').nullable(),
  lpOrder:    z.number().int().min(1, 'A ordem deve ser pelo menos 1.').nullable(),
  lpCoverage: z.string().nullable(),
  lpHolder:   z.string().nullable(),
  lpBenefits: z.array(z.string().trim().min(1).max(300)).nullable(),
  lpNotes:    z.string().nullable(),
  // Estado dos arquivos: cobre ambos os modos.
  //   - File: arquivo novo selecionado nesta sessão (cadastro ou substituição em edição)
  //   - true: já existe arquivo enviado no servidor (edição)
  //   - false/null: não há arquivo
  lpImagePresent:    z.union([z.literal(true), z.instanceof(File), z.null()]).nullable(),
  lpContractPresent: z.union([z.literal(true), z.instanceof(File), z.null()]).nullable(),
}).superRefine((val, ctx) => {
  if (val.displayWebsite) {
    if (!val.lpName || val.lpName.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['lpName'], message: 'Informe o nome do plano para a LP.' })
    }
    if (!val.lpOrder || val.lpOrder < 1) {
      ctx.addIssue({ code: 'custom', path: ['lpOrder'], message: 'Informe a ordem de exibição na LP.' })
    }
    if (!val.lpCoverage || val.lpCoverage.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['lpCoverage'], message: 'Descreva a cobertura para exibição na LP.' })
    }
    if (!val.lpHolder || val.lpHolder.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['lpHolder'], message: 'Descreva o titular/abrangência para a LP.' })
    }
    if (!val.lpBenefits || val.lpBenefits.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['lpBenefits'], message: 'Adicione pelo menos um benefício.' })
    }
    if (!val.lpImagePresent) {
      ctx.addIssue({ code: 'custom', path: ['lpImagePresent'], message: 'Envie a imagem do plano para a LP.' })
    }
    if (!val.lpContractPresent) {
      ctx.addIssue({ code: 'custom', path: ['lpContractPresent'], message: 'Envie o contrato (PDF) do plano.' })
    }
  }
})
type FormValues = z.infer<typeof schema>

export function PlanFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const { can } = useAuthority()
  const canSave = can(isEdit ? 'commercial.plans.edit' : 'commercial.plans.create')
  // Enviar/remover imagem e contrato da LP têm permissão própria.
  const canMedia = can('commercial.plans.media')
  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('basic')

  // Estado do plano carregado — necessário para uploads (precisamos do id) e para
  // mostrar URLs assinadas atuais dos arquivos.
  const [plan, setPlan] = useState<Plan | null>(null)
  const [modalities, setModalities] = useState<Modality[]>([])
  const [products, setProducts] = useState<Pick<Product, 'id' | 'name' | 'modalities'>[]>([])
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)

  const form = useZodForm(schema, {
    defaultValues: {
      name: '',
      productId: '',
      price: 0,
      planType: 'PESSOA FÍSICA',
      modalityId: '',
      status: 'ATIVO',
      displayWebsite: false,
      lpName: null,
      lpOrder: null,
      lpCoverage: null,
      lpHolder: null,
      lpBenefits: null,
      lpNotes: null,
      lpImagePresent: null,
      lpContractPresent: null,
    },
  })
  const displayWebsite = form.watch('displayWebsite')

  // Carrega modalidades para o select
  useEffect(() => {
    ModalitiesService.list({ size: 100, status: 'ATIVO' })
      .then(r => setModalities(r.data))
      .catch(() => setModalities([]))
    // Lista enxuta de produtos (id + name) — usada pelo select
    ProductsService.list({ size: 0, status: 'ATIVO' })
      .then(r => setProducts(r.data.map(p => ({ id: p.id, name: p.name, modalities: p.modalities }))))
      .catch(() => setProducts([]))
  }, [])

  // Quando o produto é escolhido, carrega a estrutura completa pra exibir
  // composição/modalidades/regras read-only (herdadas do produto).
  const productId = form.watch('productId')
  useEffect(() => {
    if (!productId) { setSelectedProduct(null); return }
    ProductsService.show(productId)
      .then(setSelectedProduct)
      .catch(() => setSelectedProduct(null))
  }, [productId])

  // Quando a modalidade muda, se o produto atualmente selecionado não a
  // disponibiliza, limpa o produto (força reseleção). Mantém consistência
  // com o filtro do Select de Produto.
  const modalityId = form.watch('modalityId')
  useEffect(() => {
    const pid = form.getValues('productId')
    if (!pid || !modalityId) return
    const p = products.find(x => x.id === pid)
    if (!p) return
    const ok = (p.modalities ?? []).some(m => m.planTypeRelationshipId === modalityId)
    if (!ok) {
      form.setValue('productId', '', { shouldValidate: form.formState.isSubmitted })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalityId, products])

  // Carrega plano em edição
  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    PlansService.show(id)
      .then((p) => {
        setPlan(p)
        form.reset({
          name:      p.name,
          productId: (p as unknown as { productId?: string | null }).productId ?? '',
          price:     p.price,
          planType:  p.planType,
          modalityId: p.planTypeRelationshipId ?? '',
          status:    p.status,
          displayWebsite:                     p.displayWebsite,
          lpName:     p.lpName,
          lpOrder:    p.lpOrder,
          lpCoverage: p.lpCoverage,
          lpHolder:   p.lpHolder,
          lpBenefits: p.lpBenefits,
          lpNotes:    p.lpNotes,
          // Sinaliza presença atual dos arquivos no servidor.
          lpImagePresent:    p.lpImageUrl    ? true : null,
          lpContractPresent: p.lpContractUrl ? true : null,
        })
      })
      .catch(() => toast.error({ title: 'Não encontrado', description: 'Plano não localizado.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  async function onSubmit(values: FormValues) {
    if (!canSave) return
    try {
      const payload = {
        name: values.name,
        product_id: values.productId,
        price: values.price,
        plan_type: values.planType,
        plan_type_relationship_id: values.modalityId,
        status: values.status,
        display_website:                        values.displayWebsite,

        // Página de vendas
        lp_name:     values.displayWebsite ? values.lpName : null,
        lp_order:    values.displayWebsite ? values.lpOrder : null,
        lp_coverage: values.displayWebsite ? values.lpCoverage : null,
        lp_holder:   values.displayWebsite ? values.lpHolder : null,
        lp_benefits: values.displayWebsite ? values.lpBenefits : null,
        lp_notes:    values.displayWebsite ? values.lpNotes : null,
      }

      // 1. Create/Update do plano (campos textuais)
      let savedId: string
      if (isEdit && id) {
        await PlansService.update(id, payload as Partial<Plan>)
        savedId = id
      } else {
        const created = await PlansService.create(payload as Partial<Plan>)
        savedId = created.id
      }

      // 2. Uploads pendentes (imagem/contrato selecionados nesta sessão)
      //    Funciona tanto no cadastro novo quanto em edição (substitui o anterior).
      //    Roda APÓS o save porque depende de `savedId`.
      const imageFile    = values.lpImagePresent    instanceof File ? values.lpImagePresent    : null
      const contractFile = values.lpContractPresent instanceof File ? values.lpContractPresent : null
      if (imageFile)    await PlansService.uploadLpImage(savedId, imageFile)
      if (contractFile) await PlansService.uploadLpContract(savedId, contractFile)

      toast.success({
        title: isEdit ? 'Plano atualizado' : 'Plano criado',
        description: isEdit
          ? 'As alterações foram salvas com sucesso.'
          : 'O novo plano foi criado com sucesso.',
      })
      // REGRA GLOBAL: após criar/atualizar volta para a listagem.
      navigate('/plans')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        plan_type_relationship_id: 'modalityId',
        plan_type: 'planType',
        display_website: 'displayWebsite',
        lp_name: 'lpName',
        lp_order: 'lpOrder',
        lp_coverage: 'lpCoverage',
        lp_holder: 'lpHolder',
        lp_benefits: 'lpBenefits',
        lp_notes: 'lpNotes',
      })
    }
  }

  // === Uploads ===
  // A imagem e o PDF são SELECIONADOS aqui (guardados no form como File) e o
  // upload de fato acontece no `onSubmit` após o create/update. Isso permite
  // que o cadastro NOVO inclua arquivos (sem precisar primeiro salvar pra obter
  // um id e depois entrar em edição).
  //
  // Em edição com arquivo já existente no servidor, `lpImagePresent === true` e
  // a URL atual vem de `plan.lpImageUrl`. Selecionar um novo File substitui o
  // anterior; clicar em remover apaga imediatamente no servidor.
  function handleSelectImage(file: File) {
    form.setValue('lpImagePresent', file, { shouldValidate: true })
  }
  async function handleRemoveImage() {
    if (id && plan?.lpImageUrl) {
      const updated = await PlansService.removeLpImage(id)
      setPlan(updated)
      toast.success({ title: 'Imagem removida', description: 'A imagem foi excluída.' })
    }
    form.setValue('lpImagePresent', null, { shouldValidate: true })
  }
  function handleSelectContract(file: File) {
    form.setValue('lpContractPresent', file, { shouldValidate: true })
  }
  async function handleRemoveContract() {
    if (id && plan?.lpContractUrl) {
      const updated = await PlansService.removeLpContract(id)
      setPlan(updated)
      toast.success({ title: 'Contrato removido', description: 'O PDF foi excluído.' })
    }
    form.setValue('lpContractPresent', null, { shouldValidate: true })
  }

  // URL de preview: prioriza arquivo selecionado nesta sessão (objectURL) sobre
  // a URL do servidor. Mantém previews ao trocar uma imagem em edição.
  const imageValue    = form.watch('lpImagePresent')
  const contractValue = form.watch('lpContractPresent')
  const imagePreviewUrl    = useImagePreviewUrl(imageValue, plan?.lpImageUrl ?? null)
  const contractFileName   = contractValue instanceof File ? contractValue.name : (plan?.lpContractPath?.split('/').pop() ?? null)
  const contractPreviewUrl = contractValue instanceof File ? null : (plan?.lpContractUrl ?? null)

  // === Erros por seção (badge + ícone vermelho) ===
  const errs = form.formState.errors
  const basicErrorCount =
    (errs.name ? 1 : 0) + (errs.price ? 1 : 0) +
    (errs.planType ? 1 : 0) + (errs.modalityId ? 1 : 0)
  const lpErrorCount =
    (errs.lpName ? 1 : 0) + (errs.lpOrder ? 1 : 0) +
    (errs.lpCoverage ? 1 : 0) + (errs.lpHolder ? 1 : 0) +
    (errs.lpBenefits ? 1 : 0) + (errs.lpNotes ? 1 : 0) +
    (errs.lpImagePresent ? 1 : 0) + (errs.lpContractPresent ? 1 : 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    )
  }

  // ============= Seção: Dados =============
  const basicSection = (
    <SectionShell title="Dados" description="Informações comerciais essenciais do plano.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome do plano"
          required
          wrapperClassName="md:col-span-2"
          {...form.register('name')}
          error={form.formState.errors.name?.message}
          placeholder="Ex.: Plano Familiar +"
        />
        <Controller
          name="price"
          control={form.control}
          render={({ field, fieldState }) => (
            <CurrencyInput
              label="Valor do plano (R$)"
              required
              value={field.value ?? null}
              onChange={(v) => field.onChange(v ?? 0)}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="planType"
          control={form.control}
          render={({ field, fieldState }) => (
            <Select
              label="Plano para"
              required
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              options={[
                { value: 'PESSOA FÍSICA',    label: 'Pessoa Física' },
                { value: 'PESSOA JURÍDICA',  label: 'Pessoa Jurídica' },
              ]}
              searchable={false}
              clearable={false}
              error={fieldState.error?.message}
            />
          )}
        />
        {/* Ordem: PRIMEIRO Modalidade, DEPOIS Produto. O produto é filtrado
            pelas modalidades disponíveis. */}
        <Controller
          name="modalityId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Select
              label="Modalidade"
              required
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              placeholder="Selecione"
              options={modalities.map(m => ({
                value: m.id,
                label: m.minAge != null ? `${m.name} (a partir de ${m.minAge} anos)` : m.name,
              }))}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="productId"
          control={form.control}
          render={({ field, fieldState }) => {
            // Mostra apenas produtos que oferecem a modalidade selecionada
            const modId = form.watch('modalityId')
            const list = modId
              ? products.filter(p =>
                (p.modalities ?? []).some(m => m.planTypeRelationshipId === modId),
              )
              : products
            return (
              <Select
                label="Produto"
                required
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || '')}
                placeholder={modId ? 'Selecione o produto' : 'Escolha a modalidade primeiro'}
                disabled={!modId}
                options={list.map(p => ({ value: p.id, label: p.name }))}
                error={fieldState.error?.message}
              />
            )
          }}
        />
        <Controller
          name="status"
          control={form.control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <StatusToggle value={field.value} onChange={field.onChange} />
            </div>
          )}
        />
      </div>
    </SectionShell>
  )

  // ============= Seção: Página de vendas (condicional) =============
  const lpSection = (
    <SectionShell
      title="Página de vendas"
      description="Conteúdo exibido na Landing Page pública deste plano."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome do plano na LP"
          required
          {...form.register('lpName')}
          error={form.formState.errors.lpName?.message}
          placeholder="Ex.: Plano Familiar Premium"
          hint="Nome comercial exibido no card da LP."
        />
        <Controller
          name="lpOrder"
          control={form.control}
          render={({ field, fieldState }) => (
            <NumberInput
              label="Ordem de exibição"
              required
              value={field.value ?? null}
              onChange={field.onChange}
              min={1}
              placeholder="Ex.: 1"
              hint="Define a posição do card na LP. Menor = mais à esquerda/topo."
              error={fieldState.error?.message}
            />
          )}
        />

        {/* Uploads — disponíveis no cadastro novo e em edição.
            O upload de fato acontece no submit (após criar/atualizar o plano). */}
        <FileUpload
          accept="image"
          label="Imagem do plano"
          required
          currentUrl={imagePreviewUrl}
          currentName={imageValue instanceof File ? imageValue.name : (plan?.lpImagePath?.split('/').pop() ?? null)}
          onSelect={handleSelectImage}
          onRemove={imageValue ? handleRemoveImage : undefined}
          disabled={!canMedia}
          hint="Capa exibida no card do plano na LP."
          error={form.formState.errors.lpImagePresent?.message}
        />
        <FileUpload
          accept="pdf"
          label="Contrato (PDF)"
          required
          currentUrl={contractPreviewUrl}
          currentName={contractFileName}
          onSelect={handleSelectContract}
          onRemove={contractValue ? handleRemoveContract : undefined}
          disabled={!canMedia}
          hint="Disponibilizado para download na LP."
          error={form.formState.errors.lpContractPresent?.message}
        />

        <Controller
          name="lpCoverage"
          control={form.control}
          render={({ field, fieldState }) => (
            <Textarea
              label="Cobertura"
              required
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value || null)}
              rows={3}
              wrapperClassName="md:col-span-2"
              placeholder="Ex.: Cobertura Completa para titular e dependentes diretos."
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="lpHolder"
          control={form.control}
          render={({ field, fieldState }) => (
            <Textarea
              label="Titular / Abrangência"
              required
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value || null)}
              rows={2}
              wrapperClassName="md:col-span-2"
              placeholder="Ex.: Titular, cônjuge e filhos até 21 anos*"
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="lpBenefits"
          control={form.control}
          render={({ field, fieldState }) => (
            <BenefitsEditor
              value={field.value ?? []}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="lpNotes"
          control={form.control}
          render={({ field, fieldState }) => (
            <Textarea
              label="Observações"
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value || null)}
              rows={3}
              wrapperClassName="md:col-span-2"
              placeholder="Texto pequeno exibido no rodapé do card (asteriscos, isenções, etc.)."
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
    </SectionShell>
  )

  // ============= Seção: Workflow =============
  const workflowSection = (
    <SectionShell
      title="Workflow"
      description="Controles do plano. As coberturas (funeral, telemedicina, etc.) agora vêm da composição do produto vinculado."
    >
      <Controller
        name="displayWebsite"
        control={form.control}
        render={({ field }) => (
          <Toggle checked={field.value} onChange={field.onChange} label="Habilitar no website (LP pública)" />
        )}
      />
    </SectionShell>
  )

  // Seção Produto (read-only): aparece só quando há produto selecionado.
  // Mostra o bundle herdado — não há nada pra editar aqui (as regras de
  // elegibilidade vivem no cadastro do Produto).
  const currentModalityId = form.watch('modalityId')
  const productSection = selectedProduct ? (
    <SectionShell
      title={`Produto: ${selectedProduct.name}`}
      description="Conteúdo herdado do produto. Para alterar, edite o produto em Configurações → Comercial → Produtos."
    >
      <div className="space-y-6">
        {/* Card de identidade do produto */}
        <div className="flex items-start gap-3 rounded-md bg-brand-50/40 ring-1 ring-brand-100 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-500 text-white">
            <Package size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900">{selectedProduct.name}</div>
            {selectedProduct.description && (
              <p className="mt-0.5 text-xs text-gray-600">{selectedProduct.description}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-gray-600">
              <span className="inline-flex items-center gap-1">
                <Layers size={11} className="text-gray-400" />
                {(selectedProduct.services ?? []).length} serviço{(selectedProduct.services ?? []).length === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1">
                <Users size={11} className="text-gray-400" />
                {(selectedProduct.modalities ?? []).length} modalidade{(selectedProduct.modalities ?? []).length === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck size={11} className="text-gray-400" />
                {(selectedProduct.modalities ?? []).reduce((acc, m) => acc + (m.degrees?.length ?? 0), 0)} regras de elegibilidade
              </span>
            </div>
          </div>
        </div>

        {/* Composição */}
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Layers size={14} className="text-gray-400" />
            <h4 className="text-sm font-semibold text-gray-800">Composição</h4>
          </div>
          {(selectedProduct.services ?? []).length === 0 ? (
            <p className="text-xs text-gray-500">Nenhum serviço incluso no produto.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(selectedProduct.services ?? []).map(s => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-3 rounded-md ring-1 ring-gray-200 bg-white px-3 py-2"
                >
                  <span className="break-words text-sm font-medium text-gray-800">{s.serviceName}</span>
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                    {s.providerName}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modalidades & regras */}
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Users size={14} className="text-gray-400" />
            <h4 className="text-sm font-semibold text-gray-800">Modalidades e regras de elegibilidade</h4>
          </div>
          {(selectedProduct.modalities ?? []).length === 0 ? (
            <p className="text-xs text-gray-500">Nenhuma modalidade configurada.</p>
          ) : (
            <div className="space-y-3">
              {(selectedProduct.modalities ?? []).map(m => {
                const isSelected = m.planTypeRelationshipId === currentModalityId
                return (
                  <div
                    key={m.id}
                    className={[
                      'rounded-md p-4 transition-colors',
                      isSelected
                        ? 'ring-2 ring-brand-500 bg-brand-50/60'
                        : 'ring-1 ring-gray-200 bg-white',
                    ].join(' ')}
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={[
                          'text-sm font-semibold',
                          isSelected ? 'text-brand-700' : 'text-gray-800',
                        ].join(' ')}>
                          {m.planTypeRelationship?.name ?? '—'}
                        </span>
                        {m.planTypeRelationship?.minAge != null && (
                          <span className="text-[11px] text-gray-500">
                            (a partir de {m.planTypeRelationship.minAge} anos)
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
                          <Check size={10} />
                          Selecionada
                        </span>
                      )}
                    </div>
                    {m.degrees.length === 0 ? (
                      <p className="text-xs text-gray-400">Sem regras de elegibilidade.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                              <th className="pb-1.5 font-medium">Grau de parentesco</th>
                              <th className="pb-1.5 font-medium">Idade mín.</th>
                              <th className="pb-1.5 font-medium">Idade máx.</th>
                              <th className="pb-1.5 font-medium">Apólices adicionais</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.degrees.map(d => (
                              <tr key={d.id} className="border-t border-gray-100">
                                <td className="py-1.5 text-gray-700">{d.degree?.degreeRelationship ?? '—'}</td>
                                <td className="py-1.5 text-gray-600">{d.minAge ?? '—'}</td>
                                <td className="py-1.5 text-gray-600">{d.maxAge ?? '—'}</td>
                                <td className="py-1.5 text-gray-600">{d.additionalPolicies > 0 ? `+${d.additionalPolicies}` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  ) : null

  // ============= FormSidemenu (Página de vendas e Produto são CONDICIONAIS) =============
  const sections: FormSection[] = [
    {
      key: 'basic', label: 'Dados', icon: SettingsIcon,
      content: basicSection,
      hasError: basicErrorCount > 0, errorCount: basicErrorCount,
    },
    ...(selectedProduct ? [{
      key: 'product', label: 'Produto', icon: SettingsIcon,
      content: productSection,
    } as FormSection] : []),
    ...(displayWebsite ? [{
      key: 'lp', label: 'Página de vendas', icon: Globe,
      content: lpSection,
      hasError: lpErrorCount > 0, errorCount: lpErrorCount,
    } as FormSection] : []),
    {
      key: 'workflow', label: 'Workflow', icon: Sparkles,
      content: workflowSection,
    },
  ]

  return (
    <form onSubmit={form.handleSubmit(onSubmit, (errs) => {
      // Direciona pra primeira seção com erro
      if (basicErrorCount > 0) setActiveSection('basic')
      else if (lpErrorCount > 0) setActiveSection('lp')
      onValidationFail(errs)
    })}>
      <PageHeader
        title={isEdit ? 'Editar plano' : 'Novo plano'}
        subtitle="Catálogo comercial de planos"
        crumbs={[{ label: 'Comercial' }, { label: 'Planos', to: '/plans' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/plans"
        actions={
          <Button type="submit" loading={form.formState.isSubmitting} iconLeft={<Save size={14} />} disabled={!canSave}
            title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} planos`}>
            Salvar
          </Button>
        }
      />

      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />
    </form>
  )
}

/**
 * Gera (e revoga) um object URL para preview de um `File` selecionado.
 * Se nenhum File novo foi selecionado, devolve `fallbackUrl` (URL do servidor).
 */
function useImagePreviewUrl(value: unknown, fallbackUrl: string | null): string | null {
  const [url, setUrl] = useState<string | null>(fallbackUrl)
  useEffect(() => {
    if (value instanceof File) {
      const obj = URL.createObjectURL(value)
      setUrl(obj)
      return () => URL.revokeObjectURL(obj)
    }
    setUrl(fallbackUrl)
  }, [value, fallbackUrl])
  return url
}

function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

/**
 * Editor inline de benefícios (array de strings).
 * - Adiciona via Enter ou botão +
 * - Remove via X em cada chip
 */
function BenefitsEditor({
  value, onChange, error,
}: {
  value: string[]
  onChange: (next: string[]) => void
  error?: string
}) {
  const [draft, setDraft] = useState('')

  function add() {
    const t = draft.trim()
    if (!t) return
    onChange([...(value ?? []), t])
    setDraft('')
  }
  function remove(idx: number) {
    onChange((value ?? []).filter((_, i) => i !== idx))
  }
  function move(from: number, to: number) {
    if (to < 0 || to >= value.length) return
    const next = [...value]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    onChange(next)
  }

  return (
    <div className="md:col-span-2 flex flex-col">
      <label className="text-xs font-medium text-gray-700 mb-1">Benefícios</label>
      <div className={cn(
        'bg-white rounded-md ring-1 p-3',
        error ? 'ring-rose-300' : 'ring-gray-200',
      )}>
        {(value?.length ?? 0) === 0 && (
          <p className="text-[12px] text-gray-400 italic mb-2">
            Nenhum benefício adicionado. Use o campo abaixo para incluir um.
          </p>
        )}
        {value && value.length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {value.map((b, i) => (
              <li key={i} className="flex items-center gap-2 bg-gray-50 rounded-md px-2 py-1.5 ring-1 ring-gray-100">
                <button
                  type="button"
                  onClick={() => move(i, i - 1)}
                  disabled={i === 0}
                  aria-label="Mover para cima"
                  className="cursor-pointer text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <GripVertical size={14} />
                </button>
                <span className="flex-1 text-[13px] text-gray-700">{b}</span>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="Remover benefício"
                  className="cursor-pointer w-6 h-6 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center"
                >
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Input
            label=""
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); add() }
            }}
            placeholder="Ex.: Consultas ilimitadas"
            wrapperClassName="flex-1"
          />
          <Button type="button" size="sm" variant="ghost" onClick={add} iconLeft={<Plus size={12} />}>
            Adicionar
          </Button>
        </div>
      </div>
      {error && <div className="text-[11px] text-rose-600 mt-1">{error}</div>}
    </div>
  )
}
